import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import "./Dropdown.css";
import PropTypes from "prop-types";
import downArrowIcon from "./img/downArrow.svg";
import Badge from "../badge/Badge";

export default function Dropdown(props) {
  const {
    label,
    placeholder,
    options,
    name,
    error,
    onChangeFunction,
    input = {},
    width,
    disabled,
    value,
    isMulti = false,
  } = props;

  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [showOptions, setShowOptions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const dropdownRef = useRef(null);
  const optionsRef = useRef(null);

  const getCurrentValue = () => value ?? input[name] ?? (isMulti ? [] : "");
  const getCurrentValues = () => {
    const currentValue = getCurrentValue();
    return Array.isArray(currentValue) ? currentValue : [];
  };

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "ongoing":
        return { background: "#EFF8FF", color: "#175CD3" };
      case "completed":
        return { background: "#ECFDF3", color: "#027A48" };
      default:
        return { background: "#F2F4F7", color: "#667085" };
    }
  };

  const handleChange = (selectedOption) => {
    if (isMulti) {
      const currentValues = getCurrentValues();
      const isSelected = currentValues.includes(selectedOption.value);
      const nextValues = isSelected
        ? currentValues.filter((item) => item !== selectedOption.value)
        : [...currentValues, selectedOption.value];

      const event = {
        target: {
          name: name,
          value: nextValues,
        },
      };

      onChangeFunction(event);
      return;
    }

    const event = {
      target: {
        name: name,
        value: selectedOption.value,
      },
    };
    setSearchValue(selectedOption.label);
    onChangeFunction(event);
    setShowOptions(false);
  };

  const handleSearch = (e) => {
    if (isMulti) return;

    setSearchValue(e.target.value);
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(e.target.value.toLowerCase())
    );
    setFilteredOptions(filtered);
    setShowOptions(true);
  };

  const toggleDropdown = () => {
    setShowOptions((prev) => !prev);
    if (!showOptions) {
      setFilteredOptions(options);
      updateDropdownPosition();
    }
  };

  const updateDropdownPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  const handleClickOutside = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      optionsRef.current &&
      !optionsRef.current.contains(event.target)
    ) {
      setShowOptions(false);
    }
  };

  useEffect(() => {
    const handleClick = (event) => handleClickOutside(event);
    const handleScrollOrResize = () => {
      if (showOptions) updateDropdownPosition();
    };

    document.addEventListener("mousedown", handleClick);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [showOptions]);

  useEffect(() => {
    if (!Array.isArray(options)) return;

    if (isMulti) {
      setSearchValue("");
      setFilteredOptions(options);
      return;
    }

    const actualValue = getCurrentValue();
    const matchedOption = options.find((opt) => opt.value === actualValue);
    setSearchValue(matchedOption?.label || "");
    setFilteredOptions(options);
  }, [value, input, name, options, isMulti]);

  const renderDropdownOptions = () => (
    <div
      ref={optionsRef}
      className="dropdown-options-portal"
      style={{
        position: "absolute",
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option, index) => {
          const statusStyle = getStatusStyle(option.status);
          const isSelected = isMulti && getCurrentValues().includes(option.value);
          return (
            <div
              key={index}
              className="dropdown-option"
              onClick={() => handleChange(option)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
            >
              {isMulti ? (
                <label className="dropdown-option-multi-label">
                  <input
                    type="checkbox"
                    className="dropdown-option-checkbox"
                    checked={isSelected}
                    readOnly
                  />
                  <span>{option.label}</span>
                </label>
              ) : (
                <span>{option.label}</span>
              )}
              {option.status && (
                <Badge
                  text={option.status}
                  background={statusStyle.background}
                  color={statusStyle.color}
                  width="fit-content"
                />
              )}
            </div>
          );
        })
      ) : (
        <div className="dropdown-no-options">No options found</div>
      )}
    </div>
  );

  return (
    <div className="text-input-container">
      {label && <span className="text-input-label">{label}</span>}
      <div className="dropdown" ref={dropdownRef}>
        <div className="dropdown-box" style={{ width: width }}>
          {isMulti ? (
            <input
              className="dropdown-box-search"
              type="text"
              value={
                options
                  .filter((opt) => getCurrentValues().includes(opt.value))
                  .map((opt) => opt.label)
                  .join(", ")
              }
              onClick={toggleDropdown}
              placeholder={placeholder}
              disabled={disabled}
              readOnly
            />
          ) : (
          <input
            className="dropdown-box-search"
            type="text"
            value={searchValue}
            onChange={handleSearch}
            onClick={toggleDropdown}
            placeholder={placeholder}
            disabled={disabled}
          />
          )}
          {showOptions &&
            ReactDOM.createPortal(renderDropdownOptions(), document.body)}
          <div className="dropdown-arrow" onClick={toggleDropdown}>
            <img src={downArrowIcon} alt="Dropdown arrow" />
          </div>
        </div>
      </div>
    </div>
  );
}

Dropdown.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  error: PropTypes.bool,
  onChangeFunction: PropTypes.func.isRequired,
  input: PropTypes.object.isRequired,
  width: PropTypes.string,
  disabled: PropTypes.bool,
  isMulti: PropTypes.bool,
};
